import { type SiteConfig, siteConfig } from '@config/site-config'
import { type locations, type nodes, type purchase_orders } from '@fulcrum/db'
import React, {
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react'

export type FlyoutData =
  | typeof locations.$inferSelect
  | typeof nodes.$inferSelect
  | typeof purchase_orders.$inferSelect
  | object

type ToggleFlyoutProps = {
  area?: string
  data?: FlyoutData
}

type OdinContextProps = {
  config: SiteConfig
  setConfig: (config: SiteConfig) => void
  showFlyout: boolean
  setShowFlyout: Dispatch<React.SetStateAction<boolean>>
  toggleFlyout: ({ area, data }: ToggleFlyoutProps) => void
  flyoutArea: string
  flyoutData: FlyoutData
  currTenant: string | undefined
  setCurrentTenant: Dispatch<SetStateAction<string | undefined>>
}

type OdinProviderProps = object

const OdinContext = createContext<OdinContextProps>(undefined!)
export function OdinProvider({ children }: PropsWithChildren<OdinProviderProps>) {
  const [config, setConfig] = useState<SiteConfig>(siteConfig)
  const [showFlyout, setShowFlyout] = useState(false)
  const flyoutArea = useRef('')
  const flyoutData = useRef({})
  const [currTenant, setCurrentTenant] = useState<string>()
  const toggleFlyout = useCallback(({ area, data }: ToggleFlyoutProps) => {
    if (area) {
      flyoutArea.current = area
    }
    if (data) flyoutData.current = data

    setShowFlyout((showFlyout) => !showFlyout)
  }, [])

  return (
    <OdinContext.Provider
      value={{
        showFlyout,
        toggleFlyout,
        setShowFlyout,
        flyoutArea: flyoutArea.current,
        flyoutData: flyoutData.current,
        currTenant,
        setCurrentTenant,
        config,
        setConfig,
      }}
    >
      {children}
    </OdinContext.Provider>
  )
}

export function useOdinContext(): OdinContextProps {
  const context = useContext(OdinContext)
  if (typeof context === 'undefined') {
    throw new Error('useOdinContext should be used within the OdinContext provider!')
  }

  return context
}
